// React hook over the adapter's `connectSessionStream` (fn-47.2).
//
// **Transport boundary (fn-47 R2):** the browser SSE primitive lives
// in the CONSUMER's adapter — this hook consumes ONLY the callback
// contract (`onEvent` / `onConnectionChange` / `close`). No transport
// reference ever enters this package (the import-boundary test
// enforces it).
//
// **Lifecycle:**
//   1. On mount (or genuine sessionId transition): reset state, open
//      the stream via `adapter.connectSessionStream`.
//   2. While open: normalize + dispatch frames into the reducer.
//   3. On unmount / session change: `handle.close()`.
//
// **Reset gating** (.flow memory: seedlive-merge-in-react-hooks):
// `reset` is scoped to a genuine sessionId TRANSITION tracked via a
// `useRef` compare-on-render — NOT to connect-effect dep changes. A
// parent re-render that produces a new seed/adapter reference must
// never wipe already-seeded state.
import { useEffect, useReducer, useRef } from 'react';
import { usePlatformAdapter } from '../context.js';
import { initialStreamState, normalizeWireEvent, reduceStream } from './reduce-stream.js';
/**
 * Subscribe to a session's live event stream (via the adapter from
 * `<PlatformProvider>`) and surface the reduced `StreamState`
 * projection: transcript messages, `RuntimeStatus`, connection state,
 * pending approval, and token totals.
 */
export function useSessionStream(inputs) {
    const adapter = usePlatformAdapter();
    const [state, dispatch] = useReducer(reduceStream, undefined, initialStreamState);
    // Session-id transition reset. Dispatching synchronously during
    // render is safe — `useReducer` allows render-time dispatch so long
    // as the call is gated on a value change (React eats no-op
    // re-renders). New sessions see clean state; same-session re-renders
    // (new seed reference, adapter identity churn) preserve it.
    const sessionIdRef = useRef(null);
    if (sessionIdRef.current !== inputs.sessionId) {
        sessionIdRef.current = inputs.sessionId;
        dispatch({ type: 'reset' });
    }
    // Seed effect: fold history into current state (idempotent via
    // per-event id dedupe). Runs whenever the `seedEvents` reference
    // changes; the reducer rebuilds `[...seed, ...alreadyLive]` so
    // chronology survives a live-before-seed race.
    const seed = inputs.seedEvents ?? null;
    useEffect(() => {
        if (seed === null || seed.length === 0)
            return;
        const events = [];
        for (const envelope of seed) {
            const ev = normalizeWireEvent(envelope);
            if (ev !== null)
                events.push(ev);
        }
        if (events.length === 0)
            return;
        dispatch({ type: 'seed', events });
    }, [seed]);
    const sessionId = inputs.sessionId;
    useEffect(() => {
        if (sessionId === null || sessionId.length === 0)
            return;
        // Optimistic `connecting` so the UI shows progress before the
        // adapter's transport reports its first state.
        dispatch({ type: 'connection', state: 'connecting' });
        let handle = null;
        try {
            handle = adapter.connectSessionStream({
                sessionId,
                onEvent: (envelope) => {
                    const event = normalizeWireEvent(envelope);
                    // Unusable payloads are skipped — the adapter owns error
                    // surfacing for malformed frames.
                    if (event === null)
                        return;
                    dispatch({ type: 'event', event, lastEventId: envelope.id });
                },
                onConnectionChange: (connectionState) => {
                    dispatch({ type: 'connection', state: connectionState });
                },
            });
        }
        catch {
            // Connection error at construction time — surface via state.
            dispatch({ type: 'connection', state: 'closed' });
            return;
        }
        return () => {
            handle?.close();
            dispatch({ type: 'connection', state: 'closed' });
        };
    }, [sessionId, adapter]);
    return state;
}
//# sourceMappingURL=use-session-stream.js.map